import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, ArrowLeft, Check, ChevronLeft, ChevronRight, Loader2, Phone } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { trackEvent, trackEventOnce, bookingOriginParams } from "@/lib/analytics";
import { addDays, dayLabel, formatLongDate, parseISO, toISO, todayISO } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";

const STEPS = ["Data", "Persone", "Orario", "Dati"];

function StepDots({ step }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-x-3 pr-1" aria-label="Fasi della prenotazione">
      {STEPS.map((label, i) => (
        <li key={label} className="flex items-center gap-2 sm:gap-3">
          <span
            className={`eyebrow text-[0.55rem] sm:text-[0.6rem] tracking-[0.18em] sm:tracking-[0.28em] transition-colors duration-500 ${
              i === step ? "text-sage" : i < step ? "text-wood/45" : "text-wood/25"
            }`}
            aria-current={i === step ? "step" : undefined}
          >
            {label}
          </span>
          {i < STEPS.length - 1 && <span className="h-px w-3 sm:w-5 bg-wood/20" aria-hidden="true" />}
        </li>
      ))}
    </ol>
  );
}

function Field({ label, children, hint, error, htmlFor }) {
  return (
    <label className="block" htmlFor={htmlFor}>
      <span className="eyebrow text-wood/60">{label}</span>
      <div className="mt-2">{children}</div>
      {hint && !error && <span className="mt-1.5 block text-xs text-foreground/50">{hint}</span>}
      {error && (
        <span className="mt-1.5 block text-xs text-destructive" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}

const inputCls =
  "w-full bg-cream border border-input px-4 py-3.5 text-base text-wood placeholder:text-wood/35 focus:border-sage focus:outline-none transition-colors duration-300";

const MONTHS_IT = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const DOW_IT = ["lun", "mar", "mer", "gio", "ven", "sab", "dom"];

/** Calendario in italiano, lunedì primo giorno. La disponibilità arriva dal backend. */
function BookingCalendar({ value, calendar, maxDate, onSelect }) {
  const today = todayISO();
  const [cursor, setCursor] = useState(() => parseISO(value || today));
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7; // lunedì = 0
    const total = new Date(year, month + 1, 0).getDate();
    const out = Array.from({ length: offset }, () => null);
    for (let d = 1; d <= total; d += 1) out.push(toISO(new Date(year, month, d)));
    return out;
  }, [year, month]);

  const stateOf = (iso) => {
    if (iso < today || iso > maxDate) return "out";
    const s = calendar[iso];
    if (s === "closed") return "closed";
    if (s === "unavailable" || s === "full") return "full";
    return "open";
  };

  const canGoBack = toISO(new Date(year, month, 1)) > today;
  const canGoNext = toISO(new Date(year, month + 1, 1)) <= maxDate;

  return (
    <div className="max-w-sm border border-wood/20 bg-cream p-5" data-testid="booking-calendar">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          disabled={!canGoBack}
          data-testid="calendar-prev-month"
          aria-label="Mese precedente"
          className="p-1.5 text-wood/60 hover:text-sage disabled:opacity-25 disabled:hover:text-wood/60 transition-colors duration-300"
        >
          <ChevronLeft size={17} strokeWidth={1.5} />
        </button>
        <p className="font-serif text-lg text-wood" data-testid="calendar-month">
          {MONTHS_IT[month]} {year}
        </p>
        <button
          type="button"
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          disabled={!canGoNext}
          data-testid="calendar-next-month"
          aria-label="Mese successivo"
          className="p-1.5 text-wood/60 hover:text-sage disabled:opacity-25 disabled:hover:text-wood/60 transition-colors duration-300"
        >
          <ChevronRight size={17} strokeWidth={1.5} />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-1 text-center">
        {DOW_IT.map((d) => (
          <span key={d} className="pb-2 text-[0.58rem] uppercase tracking-[0.14em] text-wood/40">{d}</span>
        ))}
        {cells.map((iso, i) => {
          if (!iso) return <span key={`e${i}`} />;
          const st = stateOf(iso);
          const disabled = st !== "open";
          const selected = iso === value;
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(iso)}
              data-testid={`calendar-day-${iso}`}
              aria-label={formatLongDate(iso)}
              aria-current={selected ? "date" : undefined}
              title={st === "closed" ? "Giorno di chiusura" : st === "full" ? "Nessuna disponibilità" : undefined}
              className={`aspect-square text-sm transition-colors duration-300 ${
                selected
                  ? "bg-sage text-cream"
                  : disabled
                    ? `text-wood/25 cursor-not-allowed ${st === "closed" || st === "full" ? "line-through" : ""}`
                    : "text-wood hover:bg-beige"
              }`}
            >
              {parseISO(iso).getDate()}
            </button>
          );
        })}
      </div>
      <p className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-wood/15 pt-3 text-[0.62rem] uppercase tracking-[0.12em] text-wood/45">
        <span>Barrato: chiusura o completo</span>
      </p>
    </div>
  );
}

export function ReservationWidget({ light = false, compact = false }) {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [step, setStep] = useState(0);
  const started = useRef(false);
  const [date, setDate] = useState(todayISO());
  const [guests, setGuests] = useState(2);
  const [slot, setSlot] = useState(null);
  const [serviceId, setServiceId] = useState(null);
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calendar, setCalendar] = useState({});
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    notes: "",
    privacy_accepted: false,
    marketing_consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const quickDays = useMemo(
    () => Array.from({ length: 14 }, (_, i) => addDays(todayISO(), i)),
    [],
  );

  useEffect(() => {
    api
      .get("/availability/calendar", { params: { start: todayISO(), days: 45 } })
      .then((r) => setCalendar(r.data))
      .catch(() => setCalendar({}));
  }, []);

  const loadAvailability = useCallback(async (d, g) => {
    setLoading(true);
    setAvail(null);
    try {
      const { data } = await api.get("/availability", { params: { date: d, guests: g } });
      setAvail(data);
    } catch (err) {
      setAvail({ error: apiError(err, "Non riusciamo a leggere le disponibilità.") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (step >= 2) loadAvailability(date, guests);
  }, [step, date, guests, loadAvailability]);

  const maxParty = 8;

  const goTimes = () => {
    setSlot(null);
    setServiceId(null);
    setStep(2);
  };

  const goToStep = (next) => {
    // booking_start: una sola volta quando l'utente entra davvero nel flusso.
    if (next > 0 && !started.current) {
      started.current = true;
      trackEvent("booking_start", { guest_count: guests, ...bookingOriginParams() });
    }
    setStep(next);
  };

  const validate = () => {
    const e = {};
    if (form.first_name.trim().length < 2) e.first_name = "Inserisci il nome";
    if (form.last_name.trim().length < 2) e.last_name = "Inserisci il cognome";
    if (form.phone.replace(/\D/g, "").length < 7) e.phone = "Inserisci un numero valido";
    if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(form.email)) e.email = "Inserisci un'email valida";
    if (!form.privacy_accepted) e.privacy_accepted = "Devi accettare l'informativa privacy";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/reservations", {
        date,
        service_id: serviceId,
        time: slot,
        guest_count: guests,
        ...form,
      });
      // Solo dati non personali: nessun nome, email, telefono o riferimento.
      trackEventOnce(`booking:${data.id || data.reference || slot}`, "booking_complete", {
        guest_count: guests,
        service_name: avail?.services?.find((s) => s.id === serviceId)?.name || "",
        days_ahead: Math.max(0, Math.round((parseISO(date) - parseISO(todayISO())) / 86400000)),
        ...bookingOriginParams(),
      });
      navigate("/prenotazione-confermata", { state: { reservation: data } });
    } catch (err) {
      const msg = apiError(err, "Non è stato possibile registrare la prenotazione.");
      setSubmitError(msg);
      if (err?.response?.status === 409) {
        setStep(2);
        loadAvailability(date, guests);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openServices = (avail?.services || []).filter((s) => s.slots.length > 0);
  const anyAvailable = openServices.some((s) => s.slots.some((x) => x.available));
  const allSlots = openServices.flatMap((s) => s.slots);
  const onlyTooLate =
    !anyAvailable && allSlots.length > 0 && allSlots.every((s) => s.reason === "late");

  return (
    <div
      className={`min-w-0 w-full ${light ? "text-cream" : "text-wood"} ${compact ? "" : "md:px-2"}`}
      data-testid="reservation-widget"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-wood/15">
        <StepDots step={step} />
        {step > 0 && (
          <button
            type="button"
            onClick={() => setStep(step - 1)}
            data-testid="booking-back"
            className="flex items-center gap-2 text-[0.68rem] uppercase tracking-[0.18em] text-wood/55 hover:text-sage transition-colors duration-300"
          >
            <ArrowLeft size={13} strokeWidth={1.5} /> Indietro
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="pt-8"
        >
          {step === 0 && (
            <div>
              <p className="font-serif text-2xl md:text-3xl text-wood">Quando vuoi venire?</p>
              <div className="mt-6 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 max-w-full" data-testid="quick-days">
                {quickDays.map((d) => {
                  const state = calendar[d];
                  const disabled = state === "closed" || state === "unavailable";
                  return (
                    <button
                      key={d}
                      type="button"
                      disabled={disabled}
                      onClick={() => setDate(d)}
                      data-testid={`day-${d}`}
                      className={`shrink-0 w-16 py-3 border text-center transition-colors duration-300 ${
                        date === d
                          ? "border-sage bg-sage text-cream"
                          : disabled
                            ? "border-wood/20 text-wood/45 cursor-not-allowed line-through"
                            : "border-wood/20 text-wood hover:border-sage"
                      }`}
                    >
                      <span className="block text-[0.6rem] uppercase tracking-widest opacity-70">
                        {dayLabel(d)}
                      </span>
                      <span className="block font-serif text-xl leading-tight">
                        {parseISO(d).getDate()}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-7">
                <p className="eyebrow text-wood/60">Oppure scegli una data</p>
                <div className="mt-3">
                  <BookingCalendar
                    value={date}
                    calendar={calendar}
                    maxDate={addDays(todayISO(), 44)}
                    onSelect={setDate}
                  />
                </div>
              </div>
              <p className="mt-6 text-sm text-foreground/60">{formatLongDate(date)}</p>
              <button
                type="button"
                onClick={() => goToStep(1)}
                data-testid="booking-next-guests"
                className="mt-8 w-full sm:w-auto px-9 py-4 bg-sage text-cream text-[0.7rem] uppercase tracking-[0.22em] hover:bg-wood transition-colors duration-500"
              >
                Continua
              </button>
            </div>
          )}

          {step === 1 && (
            <div>
              <p className="font-serif text-2xl md:text-3xl text-wood">Quante persone?</p>
              <div className="mt-6 grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="guest-picker">
                {Array.from({ length: maxParty }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setGuests(n)}
                    data-testid={`guests-${n}`}
                    className={`py-4 border font-serif text-xl transition-colors duration-300 ${
                      guests === n
                        ? "border-sage bg-sage text-cream"
                        : "border-wood/20 text-wood hover:border-sage"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="mt-5 text-sm text-foreground/60">
                Per gruppi più numerosi chiamaci al{" "}
                <a
                  href={`tel:${settings.phone_link || "+393311091971"}`}
                  className="text-sage link-underline"
                >
                  {settings.phone || "331 10 91 971"}
                </a>
                .
              </p>
              <button
                type="button"
                onClick={goTimes}
                data-testid="booking-next-times"
                className="mt-8 w-full sm:w-auto px-9 py-4 bg-sage text-cream text-[0.7rem] uppercase tracking-[0.22em] hover:bg-wood transition-colors duration-500"
              >
                Vedi gli orari
              </button>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="font-serif text-2xl md:text-3xl text-wood">
                Orari per {formatLongDate(date)}
              </p>
              <p className="mt-2 text-sm text-foreground/60">
                {guests} {guests === 1 ? "persona" : "persone"}
              </p>

              {loading && (
                <div className="mt-10 flex items-center gap-3 text-sm text-foreground/60">
                  <Loader2 size={16} className="animate-spin" /> Verifichiamo la disponibilità…
                </div>
              )}

              {!loading && avail?.error && (
                <div className="mt-8 border border-wood/20 bg-beige/60 p-6" data-testid="availability-error">
                  <p className="flex items-center gap-2 text-sm text-wood">
                    <AlertCircle size={16} /> {avail.error}
                  </p>
                  <button
                    type="button"
                    onClick={() => loadAvailability(date, guests)}
                    className="mt-4 text-[0.68rem] uppercase tracking-[0.18em] text-sage link-underline"
                  >
                    Riprova
                  </button>
                </div>
              )}

              {!loading && avail && !avail.error && avail.closed && (
                <div className="mt-8 border border-wood/20 bg-beige/60 p-7" data-testid="closed-state">
                  <p className="eyebrow text-sage">Non disponibile</p>
                  <p className="mt-3 font-serif text-2xl text-wood">{avail.reason || "Chiuso"}</p>
                  <p className="mt-3 text-sm text-foreground/65">
                    Prova un altro giorno oppure chiamaci: troveremo una soluzione.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(0)}
                      className="px-6 py-3 border border-wood/30 text-[0.68rem] uppercase tracking-[0.18em] text-wood hover:bg-wood hover:text-cream transition-colors duration-500"
                    >
                      Cambia data
                    </button>
                    <a
                      href={`tel:${settings.phone_link || "+393311091971"}`}
                      className="px-6 py-3 bg-sage text-cream text-[0.68rem] uppercase tracking-[0.18em] inline-flex items-center gap-2"
                    >
                      <Phone size={13} /> Chiamaci
                    </a>
                  </div>
                </div>
              )}

              {!loading && avail && !avail.closed && !avail.error && (
                <div className="mt-8 space-y-10">
                  {openServices.length === 0 && (
                    <p className="text-sm text-foreground/65" data-testid="no-services">
                      Le prenotazioni online per questa data non sono attive. Chiamaci al{" "}
                      {settings.phone || "331 10 91 971"}.
                    </p>
                  )}
                  {openServices.map((svc) => (
                    <div key={svc.id}>
                      <div className="flex items-baseline gap-4">
                        <h3 className="font-serif text-2xl text-wood">{svc.name}</h3>
                        <span className="text-xs tracking-wider text-foreground/45">
                          {svc.start_time} – {svc.end_time}
                        </span>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2" data-testid={`slots-${svc.name.toLowerCase()}`}>
                        {svc.slots.map((s) => (
                          <button
                            key={s.time}
                            type="button"
                            disabled={!s.available}
                            onClick={() => {
                              setSlot(s.time);
                              setServiceId(svc.id);
                            }}
                            data-testid={`slot-${s.time}`}
                            title={
                              s.available
                                ? `${s.remaining} coperti disponibili`
                                : s.reason === "full"
                                  ? "Completo"
                                  : s.reason === "late"
                                    ? "Troppo a ridosso del servizio"
                                    : "Non disponibile"
                            }
                            className={`min-w-[5.2rem] px-4 py-3.5 border text-sm tracking-wide transition-colors duration-300 ${
                              slot === s.time && serviceId === svc.id
                                ? "border-sage bg-sage text-cream"
                                : s.available
                                  ? "border-wood/20 text-wood hover:border-sage hover:text-sage"
                                  : "border-wood/20 text-wood/55 cursor-not-allowed line-through"
                            }`}
                          >
                            {s.time}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!anyAvailable && openServices.length > 0 && (
                    <div className="border border-wood/20 bg-beige/60 p-7" data-testid="full-state">
                      <p className="font-serif text-xl text-wood">
                        {onlyTooLate
                          ? "Le prenotazioni online per questo servizio sono chiuse."
                          : "Non ci sono più tavoli prenotabili online per questo servizio."}
                      </p>
                      <p className="mt-3 text-sm text-foreground/65">
                        {onlyTooLate
                          ? "Siamo troppo a ridosso del servizio: chiamaci, se c'è posto te lo diciamo subito. Oppure scegli un altro giorno."
                          : "Puoi provare un'altra data oppure chiamarci: a volte riusciamo a trovare posto."}
                      </p>
                      <div className="mt-5 flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => setStep(0)}
                          className="px-6 py-3 border border-wood/30 text-[0.68rem] uppercase tracking-[0.18em] text-wood"
                        >
                          Cambia data
                        </button>
                        <a
                          href={`tel:${settings.phone_link || "+393311091971"}`}
                          className="px-6 py-3 bg-sage text-cream text-[0.68rem] uppercase tracking-[0.18em] inline-flex items-center gap-2"
                        >
                          <Phone size={13} /> Chiamaci
                        </a>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={!slot}
                    onClick={() => goToStep(3)}
                    data-testid="booking-next-details"
                    className="w-full sm:w-auto px-9 py-4 bg-sage text-cream text-[0.7rem] uppercase tracking-[0.22em] hover:bg-wood transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Continua
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <form onSubmit={submit} noValidate data-testid="booking-details-form">
              <p className="font-serif text-2xl md:text-3xl text-wood">I tuoi dati</p>
              <p className="mt-2 text-sm text-foreground/60" data-testid="booking-recap">
                {formatLongDate(date)} · {slot} · {guests} {guests === 1 ? "persona" : "persone"}
              </p>

              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <Field label="Nome" htmlFor="fn" error={errors.first_name}>
                  <input
                    id="fn"
                    data-testid="input-first-name"
                    className={inputCls}
                    autoComplete="given-name"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  />
                </Field>
                <Field label="Cognome" htmlFor="ln" error={errors.last_name}>
                  <input
                    id="ln"
                    data-testid="input-last-name"
                    className={inputCls}
                    autoComplete="family-name"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  />
                </Field>
                <Field label="Telefono" htmlFor="ph" error={errors.phone}>
                  <input
                    id="ph"
                    data-testid="input-phone"
                    className={inputCls}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </Field>
                <Field label="Email" htmlFor="em" error={errors.email}>
                  <input
                    id="em"
                    data-testid="input-email"
                    className={inputCls}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field
                    label="Note, allergie, richieste"
                    htmlFor="nt"
                    hint="Facoltativo. Segnalaci intolleranze o occasioni particolari."
                  >
                    <textarea
                      id="nt"
                      data-testid="input-notes"
                      rows={3}
                      className={inputCls}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-7 space-y-4">
                <label className="flex gap-3 items-start text-sm text-foreground/75">
                  <input
                    type="checkbox"
                    data-testid="input-privacy"
                    className="mt-1 h-4 w-4 accent-[hsl(var(--sage))]"
                    checked={form.privacy_accepted}
                    onChange={(e) => setForm({ ...form, privacy_accepted: e.target.checked })}
                  />
                  <span>
                    Ho letto l'
                    <a href="/privacy" className="text-sage link-underline">
                      informativa privacy
                    </a>{" "}
                    e acconsento al trattamento dei dati per gestire la prenotazione.
                  </span>
                </label>
                {errors.privacy_accepted && (
                  <p className="text-xs text-destructive" role="alert">
                    {errors.privacy_accepted}
                  </p>
                )}
                <label className="flex gap-3 items-start text-sm text-foreground/60">
                  <input
                    type="checkbox"
                    data-testid="input-marketing"
                    className="mt-1 h-4 w-4 accent-[hsl(var(--sage))]"
                    checked={form.marketing_consent}
                    onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
                  />
                  <span>Voglio ricevere notizie sull'osteria (facoltativo).</span>
                </label>
              </div>

              {submitError && (
                <p
                  className="mt-6 flex items-start gap-2 border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
                  role="alert"
                  data-testid="booking-submit-error"
                >
                  <AlertCircle size={16} className="mt-0.5 shrink-0" /> {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit"
                className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 bg-wood text-cream text-[0.7rem] uppercase tracking-[0.22em] hover:bg-sage transition-colors duration-500 disabled:opacity-50"
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {submitting ? "Invio…" : "Invia la richiesta"}
              </button>
              <p className="mt-4 text-xs text-foreground/50">
                Riceverai subito un'email con il riepilogo della prenotazione.
              </p>
            </form>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default ReservationWidget;
